import { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../lib/api';
import { useToast } from '../hooks/useToast';
import { useTenantFormat } from '../hooks/useTenantFormat';
import { GlassCard } from '../components/GlassCard';
import { Building2, ArrowUpDown } from 'lucide-react';

const PERIODS = ['30d', '90d', 'ytd'];

export default function MultiBranch() {
  const { token } = useAuth();
  const toast = useToast();
  const { formatMoney: fmtINR } = useTenantFormat();
  const [period, setPeriod] = useState('30d');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sortKey, setSortKey] = useState('revenue_rupees');
  const [sortAsc, setSortAsc] = useState(false);

  useEffect(() => {
    if (!token) return;
    setLoading(true);
    api.multiBranchConsolidated(token, period)
      .then(setData)
      .catch(e => { if (e.status !== 401 && e.status !== 403) toast.show(e.message || 'Failed', 'error'); })
      .finally(() => setLoading(false));
  }, [token, period, toast]);

  const branches = useMemo(() => {
    if (!data?.branches) return [];
    return [...data.branches].sort((a, b) => {
      const va = a[sortKey] ?? 0;
      const vb = b[sortKey] ?? 0;
      return sortAsc ? va - vb : vb - va;
    });
  }, [data, sortKey, sortAsc]);

  const toggleSort = (k) => {
    if (sortKey === k) setSortAsc(!sortAsc);
    else { setSortKey(k); setSortAsc(false); }
  };

  const totals = data?.tenant_totals || {};
  const maxRev = Math.max(...(data?.branches || []).map(b => b.revenue_rupees || 0), 1);

  return (
    <div className="p-6 lg:p-8 max-w-6xl mx-auto space-y-5" data-testid="multi-branch-page">
      {/* Period selector */}
      <div className="flex gap-1.5">
        {PERIODS.map(p => (
          <button key={p} onClick={() => setPeriod(p)}
            className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
              period === p ? 'bg-white/10 text-white border border-white/20' : 'text-white/40 hover:text-white/60'
            }`}
          >{p.toUpperCase()}</button>
        ))}
      </div>

      {loading && (
        <div className="py-12 flex justify-center">
          <div className="w-6 h-6 border-2 border-[#F43F5E] border-t-transparent rounded-full animate-spin" />
        </div>
      )}

      {!loading && data && (
        <>
          {/* KPI cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {[
              { label: 'Revenue', val: totals.revenue, color: '#10B981' },
              { label: 'Expenses', val: totals.expenses, color: '#EF4444' },
              { label: 'Stock Value', val: totals.stock_value, color: '#6366F1' },
              { label: 'Gross Profit', val: totals.gross_profit, color: '#14B8A6' },
            ].map(k => (
              <GlassCard dark key={k.label} className="p-4">
                <p className="text-white/40 text-[10px] font-bold uppercase tracking-wider">{k.label}</p>
                <p className="text-white font-extrabold text-xl tabular-nums mt-1" style={{ color: k.color }}>{fmtINR(k.val)}</p>
                {k.label === 'Revenue' && <span className="px-2 py-0.5 rounded bg-white/8 text-white/50 text-[10px] font-bold mt-1 inline-block">{totals.sales_count ?? 0} sales</span>}
              </GlassCard>
            ))}
          </div>

          {/* Branch table */}
          <GlassCard dark className="overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-white/10">
                    {[
                      { key: 'branch_name', label: 'Branch', align: 'left' },
                      { key: 'revenue_rupees', label: 'Revenue ₹', align: 'right' },
                      { key: 'expenses_rupees', label: 'Expenses ₹', align: 'right' },
                      { key: 'current_stock_value_rupees', label: 'Stock ₹', align: 'right' },
                      { key: 'gross_profit_rupees', label: 'Gross Profit ₹', align: 'right' },
                      { key: 'sales_count', label: 'Sales', align: 'right' },
                      { key: 'top_product', label: 'Top Product', align: 'left' },
                    ].map(col => (
                      <th key={col.key}
                        onClick={() => col.key !== 'top_product' && toggleSort(col.key)}
                        className={`px-4 py-3 text-${col.align} text-white/50 text-xs font-bold uppercase tracking-wider cursor-pointer hover:text-white/80 transition-colors select-none`}
                      >
                        <span className="inline-flex items-center gap-1">
                          {col.label}
                          {sortKey === col.key && <ArrowUpDown size={10} className="text-white/40" />}
                        </span>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {branches.map(b => (
                    <tr key={b.branch_id}
                      data-testid={`mb-branch-row-${b.branch_id}`}
                      className={`border-b border-white/5 hover:bg-white/[0.02] ${b.gross_profit_rupees < 0 ? 'bg-red-500/[0.04]' : ''}`}
                    >
                      <td className="px-4 py-3 text-white font-semibold">{b.branch_name}</td>
                      <td className="px-4 py-3 text-right text-white tabular-nums">{fmtINR(b.revenue_rupees)}</td>
                      <td className="px-4 py-3 text-right text-white tabular-nums">{fmtINR(b.expenses_rupees)}</td>
                      <td className="px-4 py-3 text-right text-white tabular-nums">{fmtINR(b.current_stock_value_rupees)}</td>
                      <td className={`px-4 py-3 text-right tabular-nums font-bold ${b.gross_profit_rupees < 0 ? 'text-red-400' : 'text-emerald-400'}`}>
                        {fmtINR(b.gross_profit_rupees)}
                      </td>
                      <td className="px-4 py-3 text-right text-white tabular-nums">{b.sales_count}</td>
                      <td className="px-4 py-3 text-white/60 text-xs">{b.top_product || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </GlassCard>

          {/* Revenue bar chart (SVG) */}
          <GlassCard dark className="p-5">
            <p className="text-white/40 text-[10px] font-bold uppercase tracking-wider mb-4">Branch Revenue Distribution</p>
            <div className="space-y-2">
              {branches.map(b => (
                <div key={b.branch_id} className="flex items-center gap-3">
                  <span className="text-white/60 text-xs font-medium w-32 truncate flex-shrink-0">{b.branch_name}</span>
                  <div className="flex-1 h-5 rounded bg-white/5 overflow-hidden">
                    <div
                      className="h-full rounded bg-gradient-to-r from-[#F43F5E] to-[#F43F5E]/60 transition-all duration-500"
                      style={{ width: `${Math.max(((b.revenue_rupees || 0) / maxRev) * 100, 0.5)}%` }}
                    />
                  </div>
                  <span className="text-white/50 text-xs tabular-nums w-20 text-right flex-shrink-0">{fmtINR(b.revenue_rupees)}</span>
                </div>
              ))}
            </div>
          </GlassCard>
        </>
      )}
    </div>
  );
}
